import React, { memo, useCallback, useMemo, useState } from 'react';

import { useSelector } from 'react-redux';

import dynamic from 'next/dynamic';

import clsx from 'clsx';

import { ErrorBoundary } from '@bonnet/next/error-boundary';

import { useFeatures } from '@atc/features';

import {
    Col,
    ColoredBackground,
    LazyRender,
    Row,
    SubHeading,
} from 'reaxl';
import { DelayedImpressionObserver, sendClick, sendImpressions } from 'reaxl-analytics';

import contentAnalyticsHandlers from '@/analytics/contentAnalyticsHandlers';

import { currentPageNameDuck, globalConfigsDuck, userDuck } from '@/ducks';

import { vdpResultsDuck } from '@/ducks/vdp';

import CookieConsentModule from '@/modules/CookieConsentModule';
import PersonalizationEngineModule from '@/modules/PersonalizationEngineModule';

import LazyComponent from '@/components/LazyComponent';

const AutoFiPreQual = dynamic(() => import(/* webpackChunkName: "AutoFiPreQual" */ '@/components/vehicledetails/AutoFiPreQual'), { ssr: false });
const WestlakePreQual = dynamic(() => import(/* webpackChunkName: "WestlakePreQual" */ '@/components/Westlake/index.js'), { ssr: false });
const FordMotorCreditPreQual = dynamic(() => import(/* webpackChunkName: "FordMotorCreditPreQual" */ '@/components/vehicledetails/FordMotorCreditPreQual'), { ssr: false });

const autoFiPixallParams = {
    calculatorType: 'payment',
    eventSource: 'calculator',
    eventType: 'step',
    feature: 'Autofi',
    label: 'loan',
    vendor: 'Autofi',
};

/**
 * Render Third Party Prequal Calls to Action
 */
function PreQualCTAContainer({
    uiContext,
}) {
    const fmcPixallParams = {
        dealerCode: 'atfordcpo',
        calculatorType: 'payment',
        eventSource: 'dealership',
        eventType: 'click',
        feature: 'caca',
        label: uiContext === 'styled' ? 'text-link::saca::apply-for-financing::nds' : 'text-link::saca::apply-for-financing::price-card',
        program: 'fba',
        cdl: {
            eventProperties: {
                event: {
                    valueEventCode: 'TDV1',
                },
            },
        },
    };

    const westlakePreQualUrl = useSelector(globalConfigsDuck.selectors.getWestlakePreQualUrl);
    const autoFiPreQualUrl = useSelector(globalConfigsDuck.selectors.getAutoFiPreQualUrl);
    const fordMotorCreditPreQualSource = useSelector(globalConfigsDuck.selectors.getFordMotorCreditPreQualSource);
    const fordMotorCreditPreQualUrl = useSelector(globalConfigsDuck.selectors.getFordMotorCreditPreQualUrl);

    // thirdparty - config feature to enable/disable all third party placements
    // thirdparty_prequal - config features flag to enable/disable for individual integrations
    const {
        thirdparty: [enableThirdParty],
        thirdparty_prequal: [, {
            autofi: isAutoFiPreQualEnabled,
            ford_motor_credit: isFordMotorCreditPreQualEnabled,
            roadster: isRoadsterDREnabled,
            westlake: isWestLakePreQualEnabled,
        }],
    } = useFeatures(['thirdparty', 'thirdparty_prequal']);

    const listing = useSelector(vdpResultsDuck.selectors.getVdpActiveInventory);

    const {
        autoFiDealerCode,
        id: inventoryId,
        isRoadsterDigitalRetailing,
        isWestlakePrequal,
        pricingDetail: { salePrice = '', msrp = '' } = {},
        owner: { privateSeller: isPrivateSeller } = {},
        make,
        externalOwnerId = '',
        vin,
    } = listing;
    const hasPrice = !!(salePrice || msrp);

    const isRoadsterEnabled = isRoadsterDigitalRetailing && isRoadsterDREnabled;

    const personalization = useSelector(PersonalizationEngineModule.duck.selectors.selectPersonalization);
    const hasSuppressedCookies = useSelector(CookieConsentModule.duck.selectors.selectHasSuppressedGroup);
    const pageId = useSelector(currentPageNameDuck.selectors.getDuckState);
    const user = useSelector(userDuck.selectors.getDuckState);

    const [errors, setErrors] = useState({
        autofi: false,
        westlake: false,
        fordmotorcredit: false,
    });

    let isAutoFiEnabled = false;
    let isWestlakeEnabled = false;
    let isFordMotorCreditEnabled = false;

    if (enableThirdParty && hasPrice && !isPrivateSeller) {
        isWestlakeEnabled = isWestLakePreQualEnabled && isWestlakePrequal;
        isFordMotorCreditEnabled = isFordMotorCreditPreQualEnabled && make?.code === 'FORD' && externalOwnerId;
        isAutoFiEnabled = isAutoFiPreQualEnabled && autoFiDealerCode && !isRoadsterEnabled && (uiContext === 'styled' ? !isFordMotorCreditEnabled : true);
    }

    const onClickTracking = useCallback((epn, pixallData = {}) => {
        const data = {
            epn,
            pixallData,
            uiContext,
            inventoryId,
        };

        return (event) => sendClick(contentAnalyticsHandlers.preQualCTAClick, event, data);
    }, [uiContext, inventoryId]);

    const onFordMotorCreditPreQualClick = useCallback((epn, pixallData = {}) => {
        const data = {
            epn,
            pixallData,
            uiContext,
        };
        return (event) => sendClick(contentAnalyticsHandlers.preQualCTAClick, event, data);
    }, [uiContext]);

    const ctaClassNames = clsx({
        'margin-top-2 margin-top-sm-4': uiContext === 'styled',
        'margin-bottom-4': uiContext !== 'styled',
    });

    const handleWestlakeImpression = () => {
        sendImpressions({
            name: 'westlakeCTAImpression',
            data: {
                epn: 'Westlake Financing',
                uiContext,
            },
        });
    };

    const handleError = (component, value = true) => {
        setErrors((prevErrors) => ({
            ...prevErrors,
            [component]: value,
        }));
    };

    // Calculate total enabled components and total error
    const totalEnabled = useMemo(
        () => [isAutoFiEnabled, isWestlakeEnabled, isFordMotorCreditEnabled].filter(Boolean).length,
        [isAutoFiEnabled, isFordMotorCreditEnabled, isWestlakeEnabled]
    );
    const totalError = useMemo(() => Object.keys(errors).filter((key) => errors[key]).length, [errors]);

    // If all enabled components have errors, render nothing
    if (uiContext === 'styled' && totalEnabled === totalError) {
        return null;
    }

    const ctaContent = () => (
        <LazyComponent fallback={null}>
            <ErrorBoundary>
                {isWestlakeEnabled && (
                    <DelayedImpressionObserver
                        onChange={handleWestlakeImpression}
                        key={`preQuals-${listing?.id}-delayed-impression-observer`}
                    >
                        <WestlakePreQual
                            page={pageId}
                            className={ctaClassNames}
                            westlakePreQualUrl={westlakePreQualUrl}
                            listing={listing}
                            personalization={!hasSuppressedCookies && personalization}
                            placement={uiContext === 'styled' ? 'nds' : 'top'}
                            user={user}
                            onError={() => handleError('westlake')}
                        />
                    </DelayedImpressionObserver>
                )}
                {isAutoFiEnabled && (
                    <AutoFiPreQual
                        className={ctaClassNames}
                        onAutoFiPreQualClick={onClickTracking('Autofi', autoFiPixallParams)}
                        autoFiPreQualUrl={autoFiPreQualUrl}
                        listing={listing}
                        onError={() => handleError('autofi')}
                    />
                )}
                {isFordMotorCreditEnabled && (
                    <FordMotorCreditPreQual
                        className={ctaClassNames}
                        externalOwnerId={externalOwnerId}
                        fordMotorCreditPreQualUrl={fordMotorCreditPreQualUrl}
                        fordMotorCreditPreQualSource={fordMotorCreditPreQualSource}
                        onFordMotorCreditPreQualClick={onFordMotorCreditPreQualClick('FordMotorCredit', fmcPixallParams)}
                        vin={vin}
                        onError={() => handleError('fordmotorcredit')}
                    />
                )}
            </ErrorBoundary>
        </LazyComponent>
    );

    const ctaContentWithCopy = () => (
        <ColoredBackground
            id="prequal-section-styled"
            className="padding-5"
        >
            <Row>
                <Col
                    xs={12}
                    sm={6}
                    className="margin-bottom-2 margin-bottom-sm-0"
                >
                    <SubHeading size={300}>
                        See Your Estimated Monthly Payments
                    </SubHeading>
                    Found a car you are interested in? Know your financing options before visiting the dealership.
                </Col>

                <Col
                    xs={12}
                    sm={6}
                >
                    {ctaContent()}
                </Col>
            </Row>
        </ColoredBackground>
    );

    const lazyLoadWrapper = () => (
        <LazyRender>
            {ctaContentWithCopy()}
        </LazyRender>
    );

    // determine whether to render cta's below NDS (lazyLoadWrapper) or in right rail
    if (isWestlakeEnabled || isFordMotorCreditEnabled || isAutoFiEnabled) {
        return uiContext === 'styled' ? lazyLoadWrapper() : ctaContent();
    }

    return null;
}

export default memo(PreQualCTAContainer);
